import Point from './point';

/**
 * Representation of a distance in a two dimensional space
 *
 *     var distance = new Ofcs.Distance(125.456, 658.26645);
 *
 */
export default class Distance {
    /**
     * Point representing absolute null point
     *
     * @protected
     * @property {Point} nullPoint
     */
    nullPoint = new Point(0, 0);

    /**
     * Point representing distance
     * @property {Point} point
     */
    point = null;

    /**
     * @param {number|Array} Float for x or array with x and y
     * @param {number} Optional param with y position
     */
    constructor(...args) {
        if (args.length === 2 && args[0] instanceof Point && args[1] instanceof Point) {
            const [pointA, pointB] = args;
            this.point = new Point(pointB.x - pointA.x, pointB.y - pointA.y);
        } else {
            try {
                this.point = new Point(...args);
            } catch (error) {
                throw new Error('Distance expects two points, two floats or an array of two floats');
            }
        }
    }

    /**
     * Checks if two distances are equal
     *
     * @param {Ofcs.Distance} other Distance to check against
     * @param {number} [delta=0.0001] Delta which defines the check tolerance
     * @return {boolean}
     */
    equal(other, delta) {
        return this.point.equal(other.point, delta);
    }

    /**
     * Transforms the point by the given transform matrix
     *
     * @param {Ofcs.Matrix} matrix Transform matrix
     * @return {Ofcs.Distance}
     * @chainable
     */
    transform(matrix) {
        this.point.transform(matrix);
        return this;
    }

    /**
     * Returns angle between this point and the given point
     *
     * @return {number} Angle between points
     */
    getAngle() {
        return this.nullPoint.getAngleTo(this);
    }

    /**
     * Returns distance
     *
     * @return {number} Distance
     */
    getDistance() {
        return this.nullPoint.getDistanceTo(this.point);
    }

    /**
     * Return an array containing x and y
     *
     * This can be used to serialize and persist the point.
     * The name of this method is a bit confusing, because it
     * conforms to JavaScript's JSON API.
     *
     * @link https://developer.mozilla.org/en-US/docs/JSON#toJSON()_method
     * @return {Array} Array with x and y
     */
    toJSON() {
        return this.point.toJSON();
    }

    /**
     * Return a cloned instance of Ofcs.Distance
     *
     * @return {Ofcs.Distance}
     */
    clone() {
        return new Distance(this.point.x, this.point.y);
    }
}
